import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FiPlus, FiMinus, FiX, FiCheck, FiAlertTriangle, 
  FiInfo, FiTrash2, FiEdit2
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';

const MatrixView = () => {
  const { state, dispatch } = useSolution();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  const components = state.selectedComponents.filter(comp =>
    !searchTerm || 
    comp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    comp.phaseName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    comp.layerName?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const sortedComponents = [...components].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'price':
        comparison = (a.cost?.low || 0) - (b.cost?.low || 0);
        break;
      case 'quantity':
        comparison = (a.quantity || 1) - (b.quantity || 1);
        break;
      case 'total':
        comparison = ((a.cost?.low || 0) * (a.quantity || 1)) - ((b.cost?.low || 0) * (b.quantity || 1));
        break;
      default:
        comparison = 0;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });
  
  const handleQuantityChange = (id, newQuantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity: newQuantity } });
  };
  
  const handleRemove = (component) => {
    dispatch({ type: 'REMOVE_COMPONENT', payload: component });
  };
  
  const totalLow = sortedComponents.reduce((sum, c) => sum + (c.cost?.low || 0) * (c.quantity || 1), 0);
  const totalHigh = sortedComponents.reduce((sum, c) => sum + (c.cost?.high || 0) * (c.quantity || 1), 0);
  
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <input
              type="text"
              placeholder="Search components..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none"
            >
              <option value="name">Sort by Name</option>
              <option value="price">Sort by Price</option>
              <option value="quantity">Sort by Quantity</option>
              <option value="total">Sort by Total</option>
            </select>
            
            <button
              onClick={() => setSortOrder(order => order === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
            >
              {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
            </button>
          </div>
          
          <div className="text-right">
            <div className="text-sm text-gray-500">Total Cost</div>
            <div className="text-lg font-bold text-green-600">
              ${totalLow.toFixed(0)} - ${totalHigh.toFixed(0)}
            </div>
          </div>
        </div>
      </div>
      
      {/* Table */}
      <div className="flex-1 overflow-y-auto p-4">
        {sortedComponents.length === 0 ? (
          <div className="text-center text-gray-500 py-20">
            <p className="text-lg">No components selected</p>
            <p className="text-sm">Select components from the sidebar to view them here</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Component</th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Hierarchy</th>
                  <th className="p-4 text-center text-sm font-semibold text-gray-700">Unit Price (Low)</th>
                  <th className="p-4 text-center text-sm font-semibold text-gray-700">Unit Price (High)</th>
                  <th className="p-4 text-center text-sm font-semibold text-gray-700">Quantity</th>
                  <th className="p-4 text-center text-sm font-semibold text-gray-700">Total (Low)</th>
                  <th className="p-4 text-center text-sm font-semibold text-gray-700">Total (High)</th>
                  <th className="p-4 text-center text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedComponents.map((comp, index) => {
                  const quantity = comp.quantity || 1;
                  const totalLowCost = (comp.cost?.low || 0) * quantity;
                  const totalHighCost = (comp.cost?.high || 0) * quantity;
                  
                  return (
                    <motion.tr
                      key={comp.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-gray-100 hover:bg-blue-50 transition-colors"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{comp.icon}</span>
                          <div>
                            <div className="font-semibold text-gray-900">{comp.name}</div>
                            <div className="text-xs text-gray-500">{comp.purpose}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-xs">
                          <div className="text-blue-600">{comp.phaseName}</div>
                          <div className="text-yellow-600">→ {comp.layerName}</div>
                          <div className="text-pink-600">→ {comp.subLayerName}</div>
                        </div>
                      </td>
                      <td className="p-4 text-center text-gray-900">
                        ${comp.cost?.low?.toFixed(2) || '0.00'}
                      </td>
                      <td className="p-4 text-center text-gray-900">
                        ${comp.cost?.high?.toFixed(2) || '0.00'}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleQuantityChange(comp.id, quantity - 1)}
                            className="p-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-600"
                            disabled={quantity <= 1}
                          >
                            <FiMinus className="w-3 h-3" />
                          </button>
                          <input
                            type="number"
                            value={quantity}
                            onChange={(e) => handleQuantityChange(comp.id, parseInt(e.target.value) || 1)}
                            className="w-16 text-center border border-gray-300 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min="1"
                          />
                          <button
                            onClick={() => handleQuantityChange(comp.id, quantity + 1)}
                            className="p-1 bg-blue-500 hover:bg-blue-600 rounded text-white"
                          >
                            <FiPlus className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                      <td className="p-4 text-center font-semibold text-gray-900">
                        ${totalLowCost.toFixed(2)}
                      </td>
                      <td className="p-4 text-center font-semibold text-gray-900">
                        ${totalHighCost.toFixed(2)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => dispatch({ type: 'SHOW_COMPONENT_DETAIL', payload: comp })}
                            className="p-2 hover:bg-blue-100 rounded-lg text-blue-600"
                            title="View Details"
                          >
                            <FiInfo className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRemove(comp)}
                            className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                            title="Remove"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-200">
                  <td className="p-4 font-bold text-gray-900" colSpan={4}>
                    Total ({sortedComponents.length} components)
                  </td>
                  <td className="p-4 text-center font-bold text-gray-900">
                    {sortedComponents.reduce((sum, c) => sum + (c.quantity || 1), 0)}
                  </td>
                  <td className="p-4 text-center font-bold text-green-600">
                    ${totalLow.toFixed(2)}
                  </td>
                  <td className="p-4 text-center font-bold text-green-600">
                    ${totalHigh.toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatrixView;